import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Botones del Design System.
 *
 * REGLA: un solo botón teal (`default`) por pantalla — la acción primaria del
 * encabezado. Todo lo demás es `outline`, `ghost` o, cuando la acción tiene
 * peso dentro de un aviso, `secondary` (navy).
 *
 * Alturas: 36px encabezado · 34px toolbar · 31px dentro de fila o tarjeta.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-[7px] whitespace-nowrap rounded-lg font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        // Primario — una sola vez por pantalla.
        default:
          "bg-primary text-primary-foreground shadow-[var(--shadow-primary)] hover:bg-primary-hover",
        // Navy — acción de peso dentro de un aviso o tarjeta.
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary-hover",
        // Outline — acción secundaria y de fila.
        outline:
          "border border-input bg-background text-foreground hover:bg-border-faint",
        // Destructivo — la acción que borra, o el recordatorio masivo a atrasados.
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive-hover focus-visible:ring-destructive/40",
        // Destructivo suave — la acción que borra cuando NO es el botón
        // principal de la pantalla: zona de peligro al pie de un formulario,
        // "eliminar definitivamente" en la papelera. Rojo legible sobre fondo
        // rosado en vez de un bloque rojo saturado, que competiría con el
        // primario por la atención sin ser la acción que uno quiere fomentar.
        // Outline rojo de la zona de peligro (DS §4bis.6). Nunca rojo SÓLIDO
        // para esto: mover a la papelera es reversible y conserva la evidencia,
        // así que el botón no debe gritar más fuerte que la consecuencia real.
        // El borde va con su propio token y no con `destructive/25` — un rojo
        // con alfa cambia de tono según lo que tenga detrás, y este botón vive
        // sobre un panel que ya es rosado.
        "destructive-soft":
          "bg-destructive-bg text-destructive border border-destructive-border hover:bg-destructive-bg-hover focus-visible:ring-destructive/40",
        // Ghost — acciones repetidas dentro de una fila.
        ghost:
          "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        link: "text-primary-hover underline-offset-4 hover:underline hover:text-primary-strong",
      },
      size: {
        /** 36px — botón primario de encabezado, la medida por defecto. */
        default: "h-9 px-3.5 text-[13px]",
        /** 34px — control dentro de una toolbar. */
        sm: "h-[34px] px-3 text-[12.5px]",
        /** 31px — botón dentro de una fila o tarjeta. */
        xs: "h-[31px] px-[11px] text-[12.5px]",
        /** 40px — envío de formulario a pantalla completa (login, registro). */
        lg: "h-10 px-5 text-sm",
        /** 32px cuadrado. */
        icon: "size-8 rounded-md",
        /** 28×28 — ghost de fila, la medida del DS. */
        "icon-sm": "size-7 rounded-md",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
